import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  LocalShipping as ShippingIcon,
  AddCircleOutline as AddFundsIcon,
  AccountBalanceWallet as WalletIcon,
  SmartToy as AiIcon,
} from '@mui/icons-material';
import { useAdminUser } from '../../hooks/admin/useAdminUsers';
import { useAdminOrders } from '../../hooks/admin/useAdminOrders';
import UserOrdersTable from '../../components/admin/UserOrdersTable';
import axiosInstance from '../../services/api';
import { useQueryClient } from '@tanstack/react-query';
import {
  INK,
  FONTS,
  primaryBtnSx,
  ghostBtnSx,
  dangerBtnSx,
  ledgerInputSx,
  ledgerSelectSx,
  ledgerSwitchSx,
  editorialDialogTitleSx,
  fieldLabelSx,
} from '../../styles/ledger';
import {
  LedgerPage,
  KPIStrip,
  KPI,
  LedgerCard,
  SectionHeader,
  StatusDot,
  MonoTag,
} from '../../components/admin/LedgerPrimitives';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const formatMoney = (amount: number | string, code = 'ILS') =>
  new Intl.NumberFormat('en-IL', { style: 'currency', currency: code || 'ILS', maximumFractionDigits: 2 }).format(Number(amount || 0));

const UserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userId = id ? parseInt(id) : 0;

  const [tabValue, setTabValue] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [walletDialogOpen, setWalletDialogOpen] = useState(false);
  const [walletAmount, setWalletAmount] = useState('');
  const [walletCurrency, setWalletCurrency] = useState('ILS');
  const [walletNote, setWalletNote] = useState('');
  const [walletSubmitting, setWalletSubmitting] = useState(false);
  const [walletSuccess, setWalletSuccess] = useState<string | null>(null);
  const [walletError, setWalletError] = useState<string | null>(null);
  const [aiEnabled, setAiEnabled] = useState(false);
  // null = unlimited daily budget
  const [aiDailyTokenCap, setAiDailyTokenCap] = useState<number | null>(50000);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiSaveSuccess, setAiSaveSuccess] = useState(false);
  const [aiSaveError, setAiSaveError] = useState<string | null>(null);

  const { user, isLoading, error, updateUser, isUpdating } = useAdminUser(userId);
  const { orders, isLoading: isLoadingOrders, stats } = useAdminOrders({ user_id: userId, per_page: 5 });

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    use_for: '',
    language: '',
    company: '',
    role: '',
    is_active: true,
    is_admin: false,
  });

  React.useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        use_for: user.use_for || '',
        language: user.language || '',
        company: user.company || '',
        role: user.role || '',
        is_active: user.is_active,
        is_admin: user.is_admin,
      });
      setAiEnabled((user as any).ai_shipment_enabled ?? false);
      const cap = (user as any).ai_daily_token_cap;
      setAiDailyTokenCap(cap === null ? null : cap ?? 50000);
      setWalletCurrency(user.default_currency_code || user.wallets?.[0]?.currency_code || 'ILS');
    }
  }, [user]);

  const walletCurrencyOptions = React.useMemo(() => {
    const codes = new Set<string>();
    if (user?.default_currency_code) codes.add(user.default_currency_code);
    user?.wallets?.forEach((w) => w.currency_code && codes.add(w.currency_code));
    codes.add('ILS');
    return Array.from(codes);
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };
  const handleBooleanChange = (name: string, value: boolean) =>
    setFormData((p) => ({ ...p, [name]: value }));

  const handleEditToggle = () => {
    if (isEditing && user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        use_for: user.use_for || '',
        language: user.language || '',
        company: user.company || '',
        role: user.role || '',
        is_active: user.is_active,
        is_admin: user.is_admin,
      });
    }
    setIsEditing(!isEditing);
  };

  const handleSaveUser = async () => {
    try {
      await updateUser(formData);
      setIsEditing(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAiSave = async () => {
    if (!user) return;
    setAiSaving(true);
    setAiSaveSuccess(false);
    setAiSaveError(null);
    try {
      const { data } = await axiosInstance.patch(`/admin/users/${user.id}/ai-toggle`, {
        enabled: aiEnabled,
        daily_token_cap: aiDailyTokenCap,
      });
      setAiEnabled(data.ai_shipment_enabled);
      setAiDailyTokenCap(data.ai_daily_token_cap === null ? null : data.ai_daily_token_cap);
      setAiSaveSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['admin', 'user', Number(userId)] });
    } catch (err: any) {
      setAiSaveError(err?.response?.data?.message ?? 'Failed to save AI settings.');
    } finally {
      setAiSaving(false);
    }
  };

  const handleWalletCredit = async () => {
    if (!user) return;
    const amount = Number(walletAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setWalletError('Enter a valid amount > 0.');
      return;
    }
    setWalletSubmitting(true);
    setWalletError(null);
    try {
      const response = await axiosInstance.post(`/admin/users/${user.id}/wallet/credit`, {
        amount,
        currency_code: walletCurrency.toUpperCase(),
        note: walletNote.trim() || undefined,
      });
      const updatedWallet = response.data?.data?.wallet;
      setWalletSuccess(
        updatedWallet
          ? `Added ${formatMoney(amount, walletCurrency)}. New ${updatedWallet.currency_code} balance: ${formatMoney(updatedWallet.balance, updatedWallet.currency_code)}.`
          : 'Wallet credited.'
      );
      setWalletDialogOpen(false);
      setWalletAmount('');
      setWalletNote('');
      queryClient.invalidateQueries({ queryKey: ['admin', 'user', Number(userId)] });
    } catch (err: any) {
      setWalletError(err?.response?.data?.message ?? 'Failed to credit wallet.');
    } finally {
      setWalletSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <LedgerPage>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress size={28} sx={{ color: INK.accent }} />
        </Box>
      </LedgerPage>
    );
  }
  if (error || !user) {
    return (
      <LedgerPage>
        <Alert severity="error" sx={{ borderRadius: 0, fontFamily: FONTS.sans, mb: 2 }}>
          Error loading user data.
        </Alert>
        <Button onClick={() => navigate('/admin/users')} startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} sx={ghostBtnSx}>
          Back to users
        </Button>
      </LedgerPage>
    );
  }

  return (
    <LedgerPage eyebrowFooter={`Almaship · User · № ${String(user.id).padStart(3, '0')}`}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Button
          onClick={() => navigate('/admin/users')}
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          sx={{ ...ghostBtnSx, border: 'none', color: INK.muted, px: 1, '&:hover': { bgcolor: 'transparent', color: INK.accent, borderColor: 'transparent' } }}
        >
          Back to directory
        </Button>
        <Stack direction="row" spacing={1.5}>
          {isEditing ? (
            <>
              <Button onClick={handleEditToggle} startIcon={<CancelIcon sx={{ fontSize: 16 }} />} sx={ghostBtnSx}>
                Cancel
              </Button>
              <Button onClick={handleSaveUser} disabled={isUpdating} startIcon={<SaveIcon sx={{ fontSize: 16 }} />} sx={primaryBtnSx}>
                {isUpdating ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => { setWalletError(null); setWalletSuccess(null); setWalletDialogOpen(true); }} startIcon={<AddFundsIcon sx={{ fontSize: 16 }} />} sx={ghostBtnSx}>
                Credit wallet
              </Button>
              <Button onClick={() => setDeleteDialogOpen(true)} startIcon={<DeleteIcon sx={{ fontSize: 16 }} />} sx={ghostBtnSx}>
                Delete
              </Button>
              <Button onClick={handleEditToggle} startIcon={<EditIcon sx={{ fontSize: 16 }} />} sx={primaryBtnSx}>
                Edit
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {/* HEADER */}
      <Box sx={{ mb: 5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <Box sx={{ width: 24, height: 1, bgcolor: INK.accent }} />
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, letterSpacing: '0.18em', color: INK.accent, fontWeight: 500, textTransform: 'uppercase' }}>
            Directory · Profile
          </Typography>
        </Stack>
        <Typography sx={{ fontFamily: FONTS.serif, fontSize: { xs: 44, md: 60 }, lineHeight: 0.95, letterSpacing: '-0.02em', color: INK.text, fontWeight: 400 }}>
          {user.first_name}
          <Box component="span" sx={{ fontStyle: 'italic', color: INK.accent }}>
            {' '}{user.last_name}
          </Box>
        </Typography>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.muted, letterSpacing: '0.04em' }}>
            № {String(user.id).padStart(3, '0')}
          </Typography>
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.muted }}>{user.email}</Typography>
          <MonoTag accent={user.is_admin}>{user.is_admin ? 'Admin' : 'User'}</MonoTag>
          <StatusDot label={user.is_active ? 'Active' : 'Paused'} tone={user.is_active ? 'active' : 'inactive'} />
          <StatusDot label={user.is_verified ? 'Verified' : 'Unverified'} tone={user.is_verified ? 'info' : 'warning'} />
        </Stack>
      </Box>

      {walletSuccess && (
        <Alert severity="success" sx={{ borderRadius: 0, mb: 2, fontFamily: FONTS.sans }} onClose={() => setWalletSuccess(null)}>
          {walletSuccess}
        </Alert>
      )}
      {walletError && !walletDialogOpen && (
        <Alert severity="error" sx={{ borderRadius: 0, mb: 2, fontFamily: FONTS.sans }} onClose={() => setWalletError(null)}>
          {walletError}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: `1px solid ${INK.hairline}`, mb: 1 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{
            minHeight: 44,
            '& .MuiTabs-indicator': { bgcolor: INK.accent, height: 2 },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontFamily: FONTS.sans,
              fontWeight: 500,
              fontSize: 13.5,
              color: INK.subtle,
              minHeight: 44,
              minWidth: 100,
              '&.Mui-selected': { color: INK.text, fontWeight: 600 },
            },
          }}
        >
          <Tab label="Orders" />
          <Tab label="Profile" />
          <Tab label="Address" />
          <Tab label="Wallets" />
          <Tab label="AI agent" icon={<AiIcon fontSize="small" sx={{ fontSize: 14 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <KPIStrip cols={4}>
          <KPI label="Total orders" value={stats.total_orders} sub="all-time" />
          <KPI label="Processing" value={stats.processing_orders} sub="active" accent />
          <KPI label="Delivered" value={stats.delivered_orders} sub="completed" />
          <KPI label="Total spent" value={`$${stats.total_revenue.toFixed(2)}`} sub="lifetime" />
        </KPIStrip>
        <LedgerCard padded={false}>
          <Box sx={{ p: 2.5, borderBottom: `1px solid ${INK.hairline}` }}>
            <SectionHeader
              eyebrow="Recent"
              title="Latest orders"
              action={
                <Button onClick={() => navigate(`/admin/orders?user_id=${user.id}`)} endIcon={<ShippingIcon sx={{ fontSize: 14 }} />} sx={ghostBtnSx}>
                  View all
                </Button>
              }
            />
          </Box>
          {isLoadingOrders ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} sx={{ color: INK.accent }} />
            </Box>
          ) : orders.length > 0 ? (
            <UserOrdersTable orders={orders} />
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: FONTS.serif, fontSize: 22, color: INK.subtle }}>
                No orders yet.
              </Typography>
            </Box>
          )}
        </LedgerCard>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <LedgerCard>
          <SectionHeader number="01" eyebrow="Edit" title="User information" />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {[
              { label: 'First name', name: 'first_name' },
              { label: 'Last name', name: 'last_name' },
              { label: 'Email', name: 'email', type: 'email' },
              { label: 'Phone', name: 'phone' },
              { label: 'Company', name: 'company' },
              { label: 'Role', name: 'role' },
            ].map(({ label, name, type }) => (
              <Box key={name}>
                <Typography sx={fieldLabelSx}>{label}</Typography>
                <TextField
                  fullWidth
                  size="small"
                  name={name}
                  type={type || 'text'}
                  value={(formData as any)[name] || ''}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  sx={ledgerInputSx}
                />
              </Box>
            ))}
            <Box>
              <Typography sx={fieldLabelSx}>Use for</Typography>
              <FormControl fullWidth size="small">
                <Select name="use_for" value={formData.use_for || ''} onChange={handleSelectChange} disabled={!isEditing} sx={ledgerSelectSx}>
                  <MenuItem value="personal" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Personal</MenuItem>
                  <MenuItem value="business" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Business</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>Language</Typography>
              <FormControl fullWidth size="small">
                <Select name="language" value={formData.language || ''} onChange={handleSelectChange} disabled={!isEditing} sx={ledgerSelectSx}>
                  {['english', 'arabic', 'spanish', 'french', 'german'].map((l) => (
                    <MenuItem key={l} value={l} sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>{l.charAt(0).toUpperCase() + l.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>Status</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.is_active ? 'active' : 'inactive'}
                  onChange={(e) => handleBooleanChange('is_active', e.target.value === 'active')}
                  disabled={!isEditing}
                  sx={ledgerSelectSx}
                >
                  <MenuItem value="active" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Active</MenuItem>
                  <MenuItem value="inactive" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>Role type</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.is_admin ? 'admin' : 'user'}
                  onChange={(e) => handleBooleanChange('is_admin', e.target.value === 'admin')}
                  disabled={!isEditing}
                  sx={ledgerSelectSx}
                >
                  <MenuItem value="user" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>User</MenuItem>
                  <MenuItem value="admin" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Admin</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </LedgerCard>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <LedgerCard>
          <SectionHeader number="01" eyebrow="Address" title="Mailing address" description="Updated by the user from their profile." />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {[
              { label: 'Country', value: user.country?.name },
              { label: 'State / Province', value: user.state?.name },
              { label: 'City', value: user.city },
              { label: 'Street', value: user.street },
              { label: 'House / Building', value: user.house_no },
              { label: 'Zip / Postal code', value: user.zip_code },
            ].map(({ label, value }) => (
              <Box key={label}>
                <Typography sx={fieldLabelSx}>{label}</Typography>
                <TextField fullWidth size="small" value={value || ''} disabled sx={ledgerInputSx} />
              </Box>
            ))}
          </Box>
        </LedgerCard>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          {user.wallets?.length ? (
            user.wallets.map((wallet) => (
              <LedgerCard key={wallet.id}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <MonoTag>{wallet.currency_code}</MonoTag>
                  <StatusDot label={wallet.is_active ? 'Active' : 'Paused'} tone={wallet.is_active ? 'active' : 'inactive'} />
                </Stack>
                <Typography sx={{ fontFamily: FONTS.serif, fontSize: 36, color: INK.text, letterSpacing: '-0.02em', lineHeight: 1 }}>
                  {formatMoney(wallet.balance, wallet.currency_code)}
                </Typography>
                <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10.5, letterSpacing: '0.14em', color: INK.subtle, textTransform: 'uppercase', mt: 1 }}>
                  Current balance
                </Typography>
              </LedgerCard>
            ))
          ) : (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Alert severity="info" sx={{ borderRadius: 0, fontFamily: FONTS.sans, border: `1px solid ${INK.hairline}` }}>
                This user does not have a wallet yet. Crediting funds will create one automatically.
              </Alert>
            </Box>
          )}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <LedgerCard>
          <SectionHeader number="01" eyebrow="AI agent" title="Shipment agent access" description="Toggle access and cap daily token spend." />
          {aiSaveSuccess && (
            <Alert severity="success" sx={{ borderRadius: 0, mb: 2, fontFamily: FONTS.sans }} onClose={() => setAiSaveSuccess(false)}>
              AI settings saved.
            </Alert>
          )}
          {aiSaveError && (
            <Alert severity="error" sx={{ borderRadius: 0, mb: 2, fontFamily: FONTS.sans }} onClose={() => setAiSaveError(null)}>
              {aiSaveError}
            </Alert>
          )}
          <Stack spacing={2.5}>
            <Box
              sx={{
                border: `1px solid ${INK.hairline}`,
                borderRadius: 0.5,
                px: 2,
                py: 1.5,
                bgcolor: INK.surface,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography sx={{ fontFamily: FONTS.sans, fontSize: 14, color: INK.text, fontWeight: 500 }}>
                  Enable AI shipment agent
                </Typography>
                <Typography sx={{ fontFamily: FONTS.sans, fontSize: 12, color: INK.muted, mt: 0.25 }}>
                  Allows this user to create shipments via the AI chat interface.
                </Typography>
              </Box>
              <FormControlLabel
                control={<Switch checked={aiEnabled} onChange={(e) => setAiEnabled(e.target.checked)} sx={ledgerSwitchSx} />}
                label=""
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography sx={fieldLabelSx}>Daily token cap</Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={aiDailyTokenCap ?? ''}
                  disabled={aiDailyTokenCap === null}
                  placeholder={aiDailyTokenCap === null ? 'Unlimited' : undefined}
                  onChange={(e) => setAiDailyTokenCap(Math.max(0, parseInt(e.target.value, 10) || 0))}
                  inputProps={{ min: 0 }}
                  helperText="Maximum tokens this user can spend per day"
                  sx={ledgerInputSx}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={aiDailyTokenCap === null}
                      onChange={(e) => setAiDailyTokenCap(e.target.checked ? null : 50000)}
                      sx={ledgerSwitchSx}
                    />
                  }
                  label={
                    <Typography sx={{ fontFamily: FONTS.sans, fontSize: 12, color: INK.muted }}>
                      Unlimited
                    </Typography>
                  }
                  sx={{ mt: 0.5, ml: 0 }}
                />
              </Box>
              <Box>
                <Typography sx={fieldLabelSx}>Used today</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={(user as any)?.ai_daily_tokens_used_today ?? 0}
                  disabled
                  helperText="Resets at 00:00 UTC"
                  sx={ledgerInputSx}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={handleAiSave}
                disabled={aiSaving}
                sx={primaryBtnSx}
                startIcon={aiSaving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
              >
                {aiSaving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          </Stack>
        </LedgerCard>
      </TabPanel>

      {/* Wallet dialog */}
      <Dialog open={walletDialogOpen} onClose={() => !walletSubmitting && setWalletDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0.5 } }}>
        <DialogTitle sx={editorialDialogTitleSx}>Credit wallet</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: FONTS.sans, color: INK.muted, mt: 2 }}>
            Credit this user's wallet directly. A deposit transaction is recorded.
          </DialogContentText>
          {walletError && (
            <Alert severity="error" sx={{ borderRadius: 0, mt: 2, fontFamily: FONTS.sans }} onClose={() => setWalletError(null)}>
              {walletError}
            </Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
            <Box>
              <Typography sx={fieldLabelSx}>Amount</Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={walletAmount}
                onChange={(e) => setWalletAmount(e.target.value)}
                inputProps={{ min: 0.01, step: 0.01 }}
                sx={ledgerInputSx}
              />
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>Currency</Typography>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ display: 'none' }}>Currency</InputLabel>
                <Select value={walletCurrency} onChange={(e) => setWalletCurrency(e.target.value)} sx={ledgerSelectSx}>
                  {walletCurrencyOptions.map((c) => (
                    <MenuItem key={c} value={c} sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <Typography sx={fieldLabelSx}>Internal note (optional)</Typography>
              <TextField fullWidth size="small" multiline minRows={3} value={walletNote} onChange={(e) => setWalletNote(e.target.value)} sx={ledgerInputSx} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setWalletDialogOpen(false)} disabled={walletSubmitting} sx={ghostBtnSx}>Cancel</Button>
          <Button
            onClick={handleWalletCredit}
            disabled={walletSubmitting}
            sx={primaryBtnSx}
            startIcon={walletSubmitting ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <WalletIcon sx={{ fontSize: 16 }} />}
          >
            {walletSubmitting ? 'Crediting…' : 'Add funds'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 0.5 } }}>
        <DialogTitle sx={editorialDialogTitleSx}>Delete user</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: FONTS.sans, color: INK.muted, mt: 2 }}>
            Are you sure you want to delete this user? This action cannot be undone and all associated data will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={ghostBtnSx}>Cancel</Button>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              navigate('/admin/users');
            }}
            sx={dangerBtnSx}
            startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </LedgerPage>
  );
};

export default UserDetailPage;
